/**
 * BellaCucinaCheckoutPage — /vendor/bella-cucina/checkout
 *
 * First-vendor rollout demo. Renders a mock restaurant checkout with
 * three menu items pre-added to a basket, and embeds the
 * `/api/hustlepoints/embed.js` widget under a "Pay with Hustle Points"
 * accordion so ops can end-to-end test the redeem flow before
 * onboarding real vendors.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Coffee, Wine, Utensils, Minus, Plus, ChefHat } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const API = process.env.REACT_APP_BACKEND_URL || "";

const MENU = [
  { id: "carbonara",  name: "Spaghetti Carbonara",    price: 28, icon: Utensils },
  { id: "burrata",    name: "Burrata + Heirloom Tomato", price: 22, icon: ChefHat },
  { id: "chianti",    name: "Chianti · glass",        price: 14, icon: Wine },
  { id: "espresso",   name: "Ristretto",              price: 5,  icon: Coffee },
];

export default function BellaCucinaCheckoutPage() {
  const [cart, setCart] = useState({
    carbonara: 1, burrata: 1, chianti: 1,
  });
  const [orderRef] = useState(
    () => `bc-${Math.random().toString(36).slice(2, 8)}`);

  const total = useMemo(() =>
    Object.entries(cart).reduce((sum, [id, qty]) => {
      const item = MENU.find((m) => m.id === id);
      return sum + (item ? item.price * qty : 0);
    }, 0), [cart]);

  // Load embed.js once + render the widget whenever basket changes.
  useEffect(() => {
    const RENDER_ID = "hp-redeem-mount";
    const rerender = () => {
      if (!window.HustlePoints) return;
      window.HustlePoints.render({
        mount:      `#${RENDER_ID}`,
        vendor:     "bella-cucina",
        basket_aud: total,
        order_ref:  orderRef,
        onRedeem:   (r) => {
          toast.success(
            `Redeemed ${r.pts_redeemed.toLocaleString()} pts. ` +
            `Vendor gets $${r.vendor_settle_aud.toFixed(2)}.`);
        },
      });
    };
    if (window.HustlePoints) {
      rerender();
    } else {
      const s = document.createElement("script");
      s.src = `${API}/api/hustlepoints/embed.js`;
      s.async = true;
      s.onload = rerender;
      document.body.appendChild(s);
    }
  }, [total, orderRef]);

  const step = (id, delta) => setCart((c) => {
    const next = Math.max(0, (c[id] || 0) + delta);
    return { ...c, [id]: next };
  });

  return (
    <div data-testid="bella-cucina-checkout" className="min-h-screen bg-stone-50">
      <PlatformHeader brand={{ name: "Demo Trattoria", to: "/vendor/bella-cucina/checkout" }}
                       showBookCta={false} showCart={false} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <div className="rounded-3xl bg-gradient-to-br from-rose-500 via-amber-500 to-rose-600 text-slate-950 p-8 shadow-xl">
          <p className="text-[11px] font-black uppercase tracking-[0.32em]">Demo · First Vendor Rollout</p>
          <h1 className="mt-2 text-3xl md:text-4xl font-black">Demo Trattoria · Checkout</h1>
          <p className="mt-2 text-sm opacity-90">
            Test the Hustle Points POS embed end-to-end. This is exactly what any
            HustleVendor drops on their own checkout page.
          </p>
        </div>

        {/* Menu items with qty steppers */}
        <section className="rounded-3xl bg-white border border-stone-200 p-6 shadow-sm">
          <h2 className="text-lg font-black text-stone-900 mb-4">Your basket</h2>
          <ul className="divide-y divide-stone-100">
            {MENU.map((item) => {
              const qty = cart[item.id] || 0;
              const Icon = item.icon;
              return (
                <li key={item.id}
                    data-testid={`bc-item-${item.id}`}
                    className="py-3 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                    <Icon size={20}/>
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-stone-900">{item.name}</div>
                    <div className="text-xs text-stone-500">${item.price.toFixed(2)}</div>
                  </div>
                  <div className="flex items-center gap-2 bg-stone-100 rounded-full px-1">
                    <button onClick={() => step(item.id, -1)}
                            data-testid={`bc-minus-${item.id}`}
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-200">
                      <Minus size={14}/>
                    </button>
                    <span className="w-6 text-center font-bold" data-testid={`bc-qty-${item.id}`}>{qty}</span>
                    <button onClick={() => step(item.id, 1)}
                            data-testid={`bc-plus-${item.id}`}
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-200">
                      <Plus size={14}/>
                    </button>
                  </div>
                  <div className="w-16 text-right font-black tabular-nums">
                    ${(qty * item.price).toFixed(2)}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-stone-200">
            <div className="text-sm text-stone-500">Order · <code>{orderRef}</code></div>
            <div className="text-2xl font-black text-stone-900" data-testid="bc-total">
              ${total.toFixed(2)}
            </div>
          </div>
        </section>

        {/* HustlePoints embed */}
        <section className="rounded-3xl bg-white border border-stone-200 p-6 shadow-sm">
          <h2 className="text-lg font-black text-stone-900 mb-2">Pay with Hustle Points</h2>
          <p className="text-xs text-stone-500 mb-4">
            This widget below is the exact drop-in served from
            <code className="mx-1">/api/hustlepoints/embed.js</code>. Enter any
            member email, click "Check balance", then Redeem to test.
          </p>
          <div id="hp-redeem-mount" data-testid="bc-hp-mount" />
        </section>

        <div className="text-center text-[11px] text-stone-500">
          Real venues drop the equivalent snippet on their own checkout — see
          <a className="underline text-amber-600 mx-1" href="/legal/hustlepoints-terms">program terms</a>.
        </div>
      </div>
      <SuiteFooter/>
    </div>
  );
}
